import { useEffect, useState } from 'react'

import { EMPTY_OBJECT } from '../../constants/common'
import { useApi } from '../useApi'
import { useNotification } from '../useNotification'

import { LIST_ITEM_NAVIGATION_EMPTY } from './useListItemNavigation.constants'
import {
  getListItemNavigationNeighbours,
  isListItemNavigationItemListed,
  isListItemNavigationNotFoundError,
} from './useListItemNavigation.utils'

/**
 * Resolves the previous / next neighbours of `uuid` inside a filtered list
 * through a backend `navigation` endpoint. `loadNavigation({ options, uuid })`
 * must resolve to `{ data: { previous_uuid, next_uuid, index, total } }`;
 * `index` / `total` may be missing (older backends): then there is no counter
 * and only an explicit `index: null` marks an item that left the list.
 * `anchor` (`{ direction, nextUuid, previousUuid, uuid }`) is the item the
 * user stepped from with its neighbours at that time; when `uuid` is no
 * longer part of the list its neighbours are derived from it.
 */
export function useListItemNavigation({
  anchor = null,
  isEnabled = true,
  loadNavigation,
  uuid,
}) {
  const { handleAddErrorNotification } = useNotification()

  const api = useApi()

  const [navigation, setNavigation] = useState(null)

  const isLoaded = navigation?.uuid === uuid

  function loadNeighbours() {
    api
      .call(loadNavigation, { uuid })
      .then(response => {
        if (!response) {
          return null
        }

        if (isListItemNavigationItemListed(response.data) || !anchor) {
          return { data: response.data }
        }

        return api
          .call(loadNavigation, { uuid: anchor.uuid })
          .then(anchorResponse =>
            anchorResponse
              ? { anchorData: anchorResponse.data, data: response.data }
              : null
          )
      })
      .then(result => {
        if (result) {
          setNavigation({ ...result, uuid })
        }
      })
      .catch(error => {
        // 404 = endpoint missing or unknown uuid: the arrows just stay disabled
        if (!isListItemNavigationNotFoundError(error)) {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        }

        // A failed lookup still settles `isLoaded`, so waiters are released
        setNavigation({ data: EMPTY_OBJECT, uuid })
      })
  }

  useEffect(() => {
    if (isEnabled) {
      loadNeighbours()
    }
  }, [isEnabled, uuid])

  const neighbours = isLoaded
    ? getListItemNavigationNeighbours({ anchor, ...navigation })
    : LIST_ITEM_NAVIGATION_EMPTY

  return {
    ...neighbours,
    isLoaded,
  }
}
